<template>
  <div class="kpi-cards-container">
    <div class="kpi-card-wrapper">
      <div class="kpi-card sent-card">
        <div class="kpi-icon">
          <i class="pi pi-send"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Mensagens Enviadas') }}</h4>
          <h2>{{ formatNumber(totais.mensagens_enviadas) }}</h2>
          <div class="kpi-trend positive">
            <i class="pi pi-arrow-up"></i>
            <span>{{ t('No periodo') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="kpi-card-wrapper">
      <div class="kpi-card received-card">
        <div class="kpi-icon">
          <i class="pi pi-inbox"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Mensagens Recebidas') }}</h4>
          <h2>{{ formatNumber(totais.mensagens_recebidas) }}</h2>
          <div class="kpi-trend positive">
            <i class="pi pi-arrow-down"></i>
            <span>{{ t('No periodo') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="kpi-card-wrapper">
      <div class="kpi-card unread-card">
        <div class="kpi-icon">
          <i class="pi pi-eye-slash"></i>
        </div>
        <div class="kpi-content">
          <h4>
            <i class="pi pi-info-circle kpi-info-icon" v-tooltip="t('Chats com mensagens nao lidas pelo funcionario')"></i>
            {{ t('Chats Não Lidos') }}
          </h4>
          <h2 :class="getUnreadClass(totais.chats_nao_lidos)">
            {{ totais.chats_nao_lidos || 0 }}
          </h2>
          <div class="kpi-trend" :class="(totais.chats_nao_lidos ?? 0) > 0 ? 'negative' : 'positive'">
            <i :class="(totais.chats_nao_lidos ?? 0) > 0 ? 'pi pi-exclamation-triangle' : 'pi pi-check'"></i>
            <span>{{ totais.chats_sem_resposta || 0 }} {{ t('sem resposta') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="kpi-card-wrapper">
      <div class="kpi-card score-card">
        <div class="kpi-icon">
          <i class="pi pi-chart-bar"></i>
        </div>
        <div class="kpi-content">
          <h4>
            <i class="pi pi-info-circle kpi-info-icon" v-tooltip="t('Score composto: 60% engajamento (mensagens enviadas vs recebidas), 40% taxa de resposta (chats respondidos)')"></i>
            {{ t('Score de Performance') }}
          </h4>
          <h2 :class="getScoreClass(totais.score_geral)">
            {{ totais.score_geral || 0 }}
          </h2>
          <div class="kpi-trend" :class="getScoreTrendClass(totais.score_geral)">
            <i :class="getScoreTrendIcon(totais.score_geral)"></i>
            <span>{{ getScoreBadge(totais.score_geral) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useMessagingAnalytics } from '../../composables/useMessagingAnalytics';

const { t } = useI18n();

interface Totais {
  mensagens_enviadas?: number;
  mensagens_recebidas?: number;
  chats_nao_lidos?: number;
  chats_sem_resposta?: number;
  score_geral?: number;
}

defineProps<{
  totais: Totais;
}>();

const { getScoreBadge } = useMessagingAnalytics();

const formatNumber = (num: number | undefined | null): string => {
  if (num === undefined || num === null) return '0';
  return num.toLocaleString('pt-BR');
};

const getUnreadClass = (count: number | undefined): string => {
  if (!count || count === 0) return 'text-green';
  if (count <= 5) return 'text-orange';
  return 'text-red';
};

const getScoreClass = (score: number | undefined): string => {
  if ((score ?? 0) >= 80) return 'text-green';
  if ((score ?? 0) >= 60) return 'text-orange';
  return 'text-red';
};

const getScoreTrendClass = (score: number | undefined): string => {
  if ((score ?? 0) >= 80) return 'positive';
  if ((score ?? 0) >= 60) return 'warning';
  return 'negative';
};

const getScoreTrendIcon = (score: number | undefined): string => {
  if ((score ?? 0) >= 80) return 'pi pi-arrow-up';
  if ((score ?? 0) >= 60) return 'pi pi-minus';
  return 'pi pi-arrow-down';
};
</script>

<style scoped>
.kpi-cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 30px;
}

.kpi-card-wrapper {
  flex: 1 1 calc(20% - 16px);
  min-width: 220px;
}

@media (max-width: 1400px) {
  .kpi-card-wrapper {
    flex: 1 1 calc(33.333% - 14px);
  }
}

@media (max-width: 1000px) {
  .kpi-card-wrapper {
    flex: 1 1 calc(50% - 10px);
  }
}

@media (max-width: 768px) {
  .kpi-card-wrapper {
    flex: 1 1 100%;
  }
}

.kpi-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 20px;
  height: 100%;
  transition: all 0.3s ease;
  border-left: 4px solid;
  position: relative;
  overflow: visible;
}

.kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, currentColor, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.kpi-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12);
  transform: translateY(-3px);
}

.kpi-card:hover::before {
  opacity: 1;
}

.sent-card {
  border-left-color: #2196f3;
}
.sent-card .kpi-icon {
  background: linear-gradient(135deg, #2196f3, #42a5f5);
}

.received-card {
  border-left-color: #4caf50;
}
.received-card .kpi-icon {
  background: linear-gradient(135deg, #4caf50, #66bb6a);
}

.unread-card {
  border-left-color: #f44336;
}
.unread-card .kpi-icon {
  background: linear-gradient(135deg, #f44336, #ef5350);
}

.score-card {
  border-left-color: #9c27b0;
}
.score-card .kpi-icon {
  background: linear-gradient(135deg, #9c27b0, #ab47bc);
}

.kpi-icon {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-content h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.kpi-info-icon {
  font-size: 12px;
  color: #999;
  cursor: help;
  opacity: 0.7;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1000;
}

.kpi-info-icon:hover {
  opacity: 1;
  color: #666;
  transform: scale(1.1);
}

.kpi-content h2 {
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.kpi-content h2.text-green { color: #4caf50; }
.kpi-content h2.text-orange { color: #ff9800; }
.kpi-content h2.text-red { color: #f44336; }

.kpi-trend {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 500;
}

.kpi-trend i {
  font-size: 14px;
}

.kpi-trend.positive {
  color: #00c853;
}

.kpi-trend.warning {
  color: #ff9800;
}

.kpi-trend.negative {
  color: #ff5252;
}

.kpi-trend.positive i {
  animation: bounceUp 1s ease-in-out infinite;
}

.kpi-trend.negative i {
  animation: bounceDown 1s ease-in-out infinite;
}

@keyframes bounceUp {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

@keyframes bounceDown {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}
</style>
